// src/lib/messages/messageAlertAudio.ts
// Audible alert for workplace-messages inbox.
// Dev-sync note f8ff1ac3: "We should add a 'ding' to the messaging center when
// new messages come in." Per-sender / per-purpose sound variants are a Phase 2
// follow-up — this slice ships a single soft chime gated by a user preference.

const LS_MESSAGE_ALERT_ENABLED = "bb-messages-alert-enabled";
const MESSAGE_ALERT_SETTINGS_EVENT = "bb-messages-alert-settings-changed";

const DEFAULTS = {
  enabled: true,
  // Audible enough to notice across a sales floor but well below voice prompts.
  volume: 0.35,
};

function emitChange(): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(MESSAGE_ALERT_SETTINGS_EVENT));
  } catch {
    // ignore
  }
}

export function getMessageAlertEnabled(): boolean {
  try {
    if (typeof window === "undefined") return DEFAULTS.enabled;
    const raw = window.localStorage.getItem(LS_MESSAGE_ALERT_ENABLED);
    return raw === null ? DEFAULTS.enabled : raw === "true";
  } catch {
    return DEFAULTS.enabled;
  }
}

export function setMessageAlertEnabled(enabled: boolean): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LS_MESSAGE_ALERT_ENABLED, String(Boolean(enabled)));
    emitChange();
  } catch {
    // ignore
  }
}

export function subscribeMessageAlertSettings(onChange: (enabled: boolean) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const notify = () => onChange(getMessageAlertEnabled());
  const onCustom = () => notify();
  const onStorage = (e: StorageEvent) => {
    if (e.key && e.key !== LS_MESSAGE_ALERT_ENABLED) return;
    notify();
  };
  window.addEventListener(MESSAGE_ALERT_SETTINGS_EVENT, onCustom as EventListener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(MESSAGE_ALERT_SETTINGS_EVENT, onCustom as EventListener);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * Pure detector — exposed for tests so the hook below can stay thin.
 * Returns true when the new unread count strictly exceeds the previous one,
 * never on the initial load (`prev === null`) so we don't ding on app open.
 */
export function detectUnreadIncrease(prev: number | null, next: number): boolean {
  if (prev === null) return false;
  if (!Number.isFinite(prev) || !Number.isFinite(next)) return false;
  return next > prev;
}

type AudioCtxCtor = typeof AudioContext;

// Single shared context to avoid hitting browser per-tab limits.
let sharedCtx: AudioContext | null = null;
function getSharedAudioContext(): AudioContext | null {
  try {
    if (typeof window === "undefined") return null;
    if (sharedCtx) return sharedCtx;
    const Ctor: AudioCtxCtor | undefined =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext;
    if (!Ctor) return null;
    sharedCtx = new Ctor();
    return sharedCtx;
  } catch {
    return null;
  }
}

/**
 * Plays a soft two-note chime (A5 -> E6). Synthesized via WebAudio so no asset
 * ships with the bundle. Browser autoplay restrictions are not a concern here
 * because the user has already interacted with the app (panel must be reachable
 * for new messages to be visible at all), but we still no-op silently if the
 * context can't start.
 */
export function playMessageDing(opts?: { volume?: number }): void {
  const ctx = getSharedAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") void ctx.resume();
  } catch {
    // ignore — fall through and try to play anyway
  }
  const volume = clampVolume(opts?.volume ?? DEFAULTS.volume);
  const now = ctx.currentTime;
  playTone(ctx, 880, now, 0.09, volume);
  playTone(ctx, 1318.5, now + 0.085, 0.12, volume);
}

/**
 * HOST-4 — optional desktop toast when the tab is backgrounded.
 * Respects the same localStorage ding preference; never prompts for permission
 * (UI must call `requestDesktopMessageAlertPermission`).
 */
export function maybeShowDesktopMessageAlert(opts?: {
  title?: string;
  body?: string;
}): void {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (!getMessageAlertEnabled()) return;
    if (!document.hidden) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    new Notification(opts?.title ?? "New team message", {
      body: opts?.body ?? "Open Messenger or Firefly to catch up.",
      tag: "bb-workplace-chat-unread",
    });
  } catch {
    // ignore — Notifications can throw in restricted contexts
  }
}

export async function requestDesktopMessageAlertPermission(): Promise<
  NotificationPermission | "unsupported"
> {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    return await Notification.requestPermission();
  } catch {
    return "unsupported";
  }
}

export function getDesktopMessageAlertPermission(): NotificationPermission | "unsupported" {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission;
  } catch {
    return "unsupported";
  }
}

function clampVolume(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > 1) return 1;
  return n;
}

function playTone(ctx: AudioContext, freq: number, startAt: number, duration: number, volume: number): void {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    // Short attack/decay to avoid clicks.
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(volume, startAt + 0.01);
    gain.gain.linearRampToValueAtTime(0, startAt + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + duration + 0.02);
  } catch {
    // ignore — failed schedule is harmless
  }
}
